import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppColors } from "@/styles/colors";
import {
  Box,
  Card,
  Flex,
  Text,
  RadioGroup,
  HStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { productCreationSchema } from "../schema/products.schema";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { TextArea } from "@/components/text-area/TextArea";
import { InputField } from "@/components/input-field/InputField";
import { handleCreateProductHandler } from "../schema/handler-functions";
import { isEmpty } from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";
import { MeasurementUnits, MeasurementUnitsLabel, TipoInsumos } from "@/constants/enums";
import { SelectComponent } from "@/components/select/Select";

export const ProductCreationPage = () => {
  const [loadingCreateProduct, setLoadingCreateProduct] =
    useState<boolean>(false);
  const [selectedTipoInsumo, setSelectedTipoInsumo] = useState<string>(
    TipoInsumos.Fertilizante.toString(),
  );

  const {
    getValues,
    register,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productCreationSchema),
    defaultValues: {
      tipoProducto: 1, // Fertilizante por defecto
      unidadMedida: [MeasurementUnits.Toneladas],
    },
  });

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const handleCreateProduct = async () => {
    await handleCreateProductHandler({
      getValues,
      setLoadingCreateProduct,
      navigate,
      fetchPermissions,
      tipoProducto: Number(selectedTipoInsumo)
    });
  };

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTS);
  };

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { tipoProducto, nombre, descripcion, unidadMedida } = watchedValues || {};

    return !tipoProducto || isEmpty(nombre) || isEmpty(descripcion) || isEmpty(unidadMedida);
  }, [watchedValues]);

  const radioItems = [
    {
      label: "Fertilizante",
      value: TipoInsumos.Fertilizante.toString(),
    },
    {
      label: "Combustible",
      value: TipoInsumos.Combustible.toString(),
    },
  ];

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar producto"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateProduct}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateProduct}
        loading={loadingCreateProduct}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          {/* Tipo de insumo */}
          <Flex flexDir="column" gap="12px" marginBottom="24px">
            <Text fontWeight={700} fontSize="16px">
              Tipo de insumo
            </Text>
            <RadioGroup.Root
              value={selectedTipoInsumo}
              onValueChange={(e) => e.value && setSelectedTipoInsumo(e.value)}
            >
              <HStack gap="6">
                {radioItems.map((item) => (
                  <RadioGroup.Item key={item.value} value={item.value}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                  </RadioGroup.Item>
                ))}
              </HStack>
            </RadioGroup.Root>
          </Flex>

          <Flex gap="16px" align="start">
            {/* Nombre */}
            <Box width="250px">
              <InputField
                inputName="nombre"
                labelText="Nombre"
                control={control}
                placeholder="Ingrese el nombre del producto"
                disabled={loadingCreateProduct}
                required
              />
            </Box>

            {/* N° de Admis */}
            <Box width="250px">
              <InputField
                inputName="numAdmis"
                labelText="N° Admis"
                control={control}
                placeholder="Ingrese N° ADMIS"
                disabled={loadingCreateProduct}
              />
            </Box>

            <Box width="250px">
                <SelectComponent 
                  items={Object.values(MeasurementUnits).map((unit) => ({
                    label: MeasurementUnitsLabel[unit as MeasurementUnits],
                    value: unit,
                  }))}
                  control={control}
                  name="unidadMedida"
                  labelText="Unidad de medida"
                  disabled={loadingCreateProduct}
                  required
                />
            </Box>
          </Flex>
        </Card.Body>
      </Card.Root>

      {/* Descripción */}
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Descripción
        </Text>
        <TextArea
          errors={errors}
          inputName="descripcion"
          maxLength={100}
          register={register}
          placeholder="Ingrese una descripción del producto"
          helperText="Máximo 100 caracteres"
          disabled={loadingCreateProduct}
          required
        />
      </Flex>
    </Flex>
  );
};

